<template>
  <Dialog
    :open="!closing"
    @update:open="handleUpdateOpen"
    variant="task-detail"
    background="white"
    :size="maxTablet ? 'full' : undefined"
    :side="maxTablet ? 'default' : 'right'"
    :dismissible="!busy"
    :showFooter="maxTablet"
  >
    <template #header>
      <TaskHeader
        :task="task"
        :canEdit="!hasLimitedAccess"
        :canLogTime="canLogTime"
        :canLogMaterial="canLogMaterial"
        :canCreateChecklist="canCreateChecklist"
        :canComplete="canCompleteTasks"
        :isArchived="isArchived"
        :readonly="readonly"
        :busy="busy"
        @update="handleUpdate"
        @logTime="openLogTime = true"
        @logMaterial="openLogMaterial = true"
        @addChecklist="addChecklistRef?.openModal()"
        @translate="handleTranslate"
        @delete="handleDelete"
        @primaryAction="handlePrimaryAction"
      />
    </template>
    <template #content>
      <TaskDetails
        :tab="tab"
        :task="task"
        :feed="feed"
        :users="users"
        :canEdit="!hasLimitedAccess"
        :canLogTime="canLogTime"
        :canLogMaterial="canLogMaterial"
        :isArchived="isArchived"
        :readonly="readonly"
        @update="handleUpdate"
        @logTime="openLogTime = true"
        @showTimeLog="openTimeLog = true"
        @logMaterial="openLogMaterial = true"
        @showMaterialLog="openMaterialLog = true"
      />
      <TaskLogTime
        v-if="canLogTime && !readonly"
        v-model:open="openLogTime"
        @update:open="(open) => !open && (_errors = {})"
        :task="task"
        :users="users"
        :errors="_errors"
        :busy="busy"
        @logTime="handleLogTime"
      />
      <TaskTimeLog
        v-model:open="openTimeLog"
        :task="task"
        :canLogTime="canLogTime && !readonly"
        @logTime="openLogTime = true"
      />
      <TaskLogMaterial
        v-if="canLogMaterial && !readonly"
        v-model:open="openLogMaterial"
        @update:open="(open) => !open && (_errors = {})"
        :task="task"
        :users="users"
        :errors="_errors"
        :busy="busy"
        @logMaterial="handleLogMaterial"
      />
      <TaskMaterialLog
        v-model:open="openMaterialLog"
        :task="task"
        :canLogMaterial="canLogMaterial && !readonly"
        @logMaterial="openLogMaterial = true"
      />
      <AddChecklistModal
        v-if="canCreateChecklist"
        ref="addChecklistRef"
        :task="task"
      />
    </template>
    <template v-if="maxTablet" #footer>
      <TaskMobileNav
        v-model:tab="tab"
        :task="task"
        :canEdit="!hasLimitedAccess"
        :canComplete="canCompleteTasks"
        :busy="busy"
        @primaryAction="handlePrimaryAction"
      />
    </template>
  </Dialog>
</template>

<script setup>
import route from "ziggy-js";
import { router } from "@inertiajs/vue3";
import { computed, inject, provide, ref } from "vue";
import { sortBy } from "lodash";
import { Dialog } from "@/SharedComponents/Dialog";
import {
  TaskHeader,
  TaskMobileNav,
  AddChecklistModal,
} from "@/SharedComponents/Tasks/TaskDetailsParts";
import {
  TaskDetails,
  TaskLogMaterial,
  TaskLogTime,
  TaskMaterialLog,
  TaskTimeLog,
} from "@/SharedComponents/Tasks";
import { toast } from "@/SharedComponents/Toaster";
import { useActivityHistory } from "@/Composables/useActivityHistory";
import { useBreakpoints } from "@/Composables/useBreakpoints";
import { useTaskChecklist } from "@/Composables/useTaskChecklist";
import { useUsers } from "@/Composables/useUsers";
import { useI18n } from "vue-i18n";
import { toUTC } from "@/Utils/date";

const { maxTablet } = useBreakpoints();
const { t } = useI18n();

const emit = defineEmits(["close"]);

const props = defineProps({
  task: { type: Object, required: true },
});

const closing = ref(false);
const tab = ref(null);
const openLogTime = ref(false);
const openTimeLog = ref(false);
const openLogMaterial = ref(false);
const openMaterialLog = ref(false);
const addChecklistRef = ref(null);
const _errors = ref({});
const busy = ref(false);

const { isLoading: isLoadingFeedData, data: feedData } = useActivityHistory(
  props.task.tenant.id,
  props.task
);

const feed = computed(() => {
  if (isLoadingFeedData.value) {
    return [];
  }
  return feedData.value.pages.flatMap((p) => p.data);
});

const { isLoading: isLoadingUserData, data: userData } = useUsers([
  props.task.tenant.id,
]);

const users = computed(() => {
  if (isLoadingUserData.value) {
    return [];
  }
  return sortBy(userData.value, "fullname");
});

const emitter = inject("emitter");

const { hasPermission, permissions } = inject("permissions");

const hasLimitedAccess = computed(() =>
  hasPermission(permissions.limitedAccess)
);
const canLogTime = computed(() =>
  hasPermission(permissions.createTime, props.task.tenant.id)
);
const canLogMaterial = computed(() =>
  hasPermission(permissions.createMaterial, props.task.tenant.id)
);
const canCreateChecklist = computed(() =>
  hasPermission(permissions.createChecklist, props.task.tenant.id)
);
const canCompleteTasks = computed(() =>
  hasPermission(permissions.completeTask, props.task.tenant.id)
);

const isArchived = computed(() => props.task.deleted_at);
const readonly = computed(() => isArchived.value || props.task.completed);

function handleUpdateOpen(value) {
  if (!value) {
    closing.value = true;
    setTimeout(() => (closing.value = false), 400);
    emit("close");
  }
}

function handleLogTime({ start, end, description, user }) {
  if (readonly.value || busy.value) {
    return;
  }

  router.post(
    route(`tasks.time.store`),
    {
      task_id: props.task.id,
      start,
      end,
      description,
      logged_for_id: user,
    },
    {
      onStart: () => {
        busy.value = true;
      },
      onSuccess: () => {
        openLogTime.value = false;
        toast.success(t(`toasts.tasks.log_time.success`));
        _errors.value = {};
        emitter.emit("reload-task-details", props.task);
      },
      onError: (errors) => {
        _errors.value = errors;
      },
      onFinish: () => {
        busy.value = false;
      },
    }
  );
}

function handleLogMaterial({ date, cost, user, description, files }) {
  if (readonly.value || busy.value) {
    return;
  }

  router.post(
    route(`tasks.material.store`),
    {
      task_id: props.task.id,
      date,
      cost: cost.replace(",", "."),
      description,
      logged_for_id: user,
      documents: files.map((file) => file.document),
    },
    {
      onStart: () => {
        busy.value = true;
      },
      onSuccess: () => {
        openLogMaterial.value = false;
        toast.success(t(`toasts.tasks.log_material.success`));
        _errors.value = {};
        emitter.emit("reload-task-details", props.task);
      },
      onError: (errors) => {
        _errors.value = errors;
      },
      onFinish: () => {
        busy.value = false;
      },
    }
  );
}

const showOriginal = ref({
  description: false,
  remarks: false,
});
provide("showOriginal", showOriginal);

function handleTranslate() {
  if (busy.value) {
    return;
  }

  router.post(
    route("settings.tasks.translate", [props.task.tenant, props.task]),
    {},
    {
      onStart: () => (busy.value = true),
      onSuccess: () => {
        showOriginal.value.description = false;
        showOriginal.value.remarks = false;
      },
      onFinish: () => (busy.value = false),
    }
  );
}

function handleDelete() {
  if (isArchived.value || busy.value || hasLimitedAccess.value) {
    return;
  }

  router.delete(route("tasks.delete", [props.task]), {
    onStart: () => {
      busy.value = true;
    },
    onSuccess: () => {
      toast.success(t("toasts.tasks.delete.success"));
      handleUpdateOpen(false);
    },
    onError: () => {
      toast.error(t("toasts.tasks.delete.error"));
    },
    onFinish: () => {
      busy.value = false;
    },
  });
}

const { checkAllItems } = useTaskChecklist(computed(() => props.task));

function handlePrimaryAction(action) {
  if (busy.value) {
    return;
  }

  if (action === "complete") {
    checkAllItems();
  }

  router.post(
    route(`tasks.${action}`, props.task),
    {},
    {
      onStart: () => {
        busy.value = true;
      },
      onSuccess: () => {
        toast.success(t(`toasts.tasks.${action}.success`));

        emitter.emit("reload-task-details", props.task);
      },
      onError: () => {
        toast.error(t(`toasts.tasks.${action}.error`));
      },
      onFinish: () => {
        busy.value = false;
      },
    }
  );
}

const formData = computed(() => ({
  category_id: props.task.category?.id,
  assignee_id: props.task.assignees.map((assignee) => assignee.id),
  schedule_for: props.task.scheduled_for
    ? toUTC(props.task.scheduled_for)
    : null,
  priority: props.task.priority.id,
  is_private: props.task.problem.is_private,
  description: props.task.description,
  remarks: props.task.remarks,
  space_id: props.task.space?.id,
  tag_ids: props.task.tags.map((tag) => tag.id),
}));

function handleUpdate(data) {
  if (
    isArchived.value ||
    (readonly.value && !Object.hasOwn(data, "tag_ids")) ||
    busy.value ||
    (hasLimitedAccess.value && !Object.hasOwn(data, "schedule_for"))
  ) {
    return;
  }

  router.put(
    route("tasks.update", [props.task]),
    {
      ...formData.value,
      ...data,
    },
    {
      preserveScroll: true,
      onStart: () => {
        busy.value = true;
      },
      onSuccess: () => {
        toast.success(t("toasts.tasks.update.success"));

        emitter.emit("reload-task-details", props.task);
      },
      onError: () => {
        toast.error(t("toasts.tasks.update.error"));
      },
      onFinish: () => {
        busy.value = false;
        router.reload();
      },
    }
  );
}
</script>
